import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Monitor, MapPin, Users } from 'lucide-react';
import { getAllPosts, addPost, updatePost, deletePost } from '../data/contentDatabase';
import { getAttendeesForMeeting, updateMeetingAttendee, removeMeetingAttendee } from '../data/meetingAttendeesDatabase';
import { getDisplayName } from '../utils/userUtils';
import { useAuth } from './useAuth';
import { Timestamp, onSnapshot, query, collection, where, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Meeting, MeetingFormData, defaultFormData } from '../models/Content';
import { User } from '../models/User';

interface MeetingAttendee {
  id: string;
  meetingId: string;
  userId: string;
  userName: string;
  attendanceType: 'in-person' | 'virtual' | 'not-attending';
  attendedAt: any;
}

const meetingTypes = [
  { value: 'AGM', label: 'Annual General Meeting' },
  { value: 'EGM', label: 'Extraordinary General Meeting' },
  { value: 'Information', label: 'Information Meeting' },
  { value: 'Norway AGM', label: 'Norway Annual General Meeting' },
  { value: 'Norway EGM', label: 'Norway Extraordinary General Meeting' }
] as const;

type MeetingType = typeof meetingTypes[number]['value'];

export const useMeetings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past' | 'my-attendance'>('upcoming');
  const [userAttendance, setUserAttendance] = useState<Record<string, 'in-person' | 'virtual' | 'not-attending'>>({});
  const [items, setItems] = useState<Meeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Real-time listener for meetings
  useEffect(() => {
    if (!user) {
      setItems([]);
      setUserAttendance({});
      setIsLoading(false);
      return;
    }

    console.log('🔄 Setting up real-time meetings listener');
    
    try {
      setIsLoading(true);
      
      // Set up real-time listener for meetings
      const postsRef = collection(db, 'posts');
      const meetingsQuery = query(
        postsRef,
        where('category', '==', 'meeting'),
        orderBy('created_at', 'desc')
      );

      const unsubscribe = onSnapshot(meetingsQuery, async (snapshot) => {
        console.log('📡 Real-time meetings update received', { 
          size: snapshot.size,
          changes: snapshot.docChanges().length 
        });
        
        if (!mountedRef.current) return;

        try {
          // Get meetings from snapshot
          const meetings = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id,
            created_at: doc.data().created_at || Timestamp.now(),
            category: 'meeting',
            authorId: doc.data().authorId || 'unknown'
          })) as Meeting[];

          // Get user attendance for all meetings in parallel
          const attendancePromises = meetings
            .filter((m): m is Meeting => m.id !== undefined)
            .map(async (meeting) => {
              const attendees = await getAttendeesForMeeting(meeting.id!);
              const userAttendance = attendees.find((a: MeetingAttendee) => a.userId === user.id);
              return {
                meetingId: meeting.id!,
                attendance: userAttendance?.attendanceType as 'in-person' | 'virtual' | 'not-attending' | undefined,
                hasRecord: !!userAttendance
              };
            });
          
          const attendanceData = await Promise.all(attendancePromises);

          if (mountedRef.current) {
            setItems(meetings);
            
            const attendance: Record<string, 'in-person' | 'virtual' | 'not-attending'> = {};
            attendanceData.forEach(({ meetingId, attendance: type, hasRecord }) => {
              // Only set attendance if user has actually made a choice (has a record)
              // Unconfirmed users (no record) won't appear in this object, showing yellow "Attending?" 
              if (hasRecord && type) {
                attendance[meetingId] = type;
              }
            });
            
            setUserAttendance(attendance);
            setError(null);
          }
        } catch (err) {
          console.error('Error processing meetings snapshot:', err);
          if (mountedRef.current) {
            setError(err instanceof Error ? err.message : 'Failed to load meetings');
          }
        } finally {
          if (mountedRef.current) {
            setIsLoading(false);
          }
        }
      }, (err) => {
        console.error('Error in meetings listener:', err);
        
        // Handle specific network errors
        if (err.code === 'unavailable' || err.message.includes('QUIC') || err.message.includes('net::ERR')) {
          console.log('🔄 Network connection issue detected, will retry automatically');
          // Don't set error state for network issues - Firestore will retry automatically
          if (mountedRef.current) {
            setIsLoading(false);
          }
        } else {
          // Only set error for non-network issues
          if (mountedRef.current) {
            setError(err.message || 'Failed to listen for meetings updates');
            setIsLoading(false);
          }
        }
      });

      unsubscribeRef.current = unsubscribe;

    } catch (err) {
      console.error('Error setting up meetings listener:', err);
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to setup meetings listener');
        setIsLoading(false);
      }
    }

    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [user?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const handleSubmit = async (formData: MeetingFormData, attachments: any[] = []) => {
    if (!user) return;
    try {
      const meeting: Meeting = {
        ...formData,
        category: 'meeting',
        authorId: user.id,
        created_at: Timestamp.fromDate(new Date()),
        attachments
      };
      
      // Just save to database - real-time listener will update the UI
      await addPost(meeting);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create meeting');
      throw err; // Re-throw so calling component can handle
    }
  };

  const handleEdit = async (id: string, formData: MeetingFormData, attachments: any[] = []) => {
    if (!user) return;
    
    const originalMeeting = items.find(m => m.id === id);
    if (!originalMeeting) return;
    
    try {
      const updatedMeeting: Meeting = {
        ...originalMeeting,
        ...formData,
        created_at: Timestamp.fromDate(new Date()),
        attachments: attachments  // Use the passed attachments directly (don't merge with old ones)
      };
      
      // Just save to database - real-time listener will update the UI
      await updatePost(updatedMeeting);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update meeting');
      throw err; // Re-throw so calling component can handle
    }
  };

  const handleDelete = async (id: string) => {
    if (!user) return;
    
    const meetingToDelete = items.find(m => m.id === id);
    if (!meetingToDelete) return;
    
    try {
      // Just delete from database - real-time listener will update the UI
      await deletePost(meetingToDelete);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete meeting');
      throw err; // Re-throw so calling component can handle
    }
  };

  const handleAttendanceChange = async (meetingId: string, attendanceType: 'in-person' | 'virtual' | 'not-attending' | 'unconfirmed') => {
    if (!user) return;
    
    // Store previous state for rollback
    const previousAttendance = userAttendance[meetingId];
    
    try {
      // If setting to unconfirmed, remove the attendance record entirely
      if (attendanceType === 'unconfirmed') {
        const newAttendance = { ...userAttendance };
        delete newAttendance[meetingId];
        setUserAttendance(newAttendance);
        
        // Remove from database
        await removeMeetingAttendee(meetingId, user.id);
      } else {
        // Optimistically update the UI immediately
        const newAttendance = {
          ...userAttendance,
          [meetingId]: attendanceType
        };
        setUserAttendance(newAttendance);
        
        // Update the database in the background
        await updateMeetingAttendee(meetingId, user, attendanceType);
      }
      
    } catch (err) {
      // Revert the optimistic update on error
      const revertedAttendance = {
        ...userAttendance,
        [meetingId]: previousAttendance
      };
      setUserAttendance(revertedAttendance);
      setError(err instanceof Error ? err.message : 'Failed to update attendance');
    }
  };

  const getFilteredMeetings = useCallback((user: User | null) => {
    if (!user) return [];
    
    const now = new Date();
    
    switch (activeTab) {
      case 'my-attendance':
        return items.filter(meeting => meeting && meeting.id && userAttendance[meeting.id] && userAttendance[meeting.id] !== 'not-attending');
      case 'upcoming':
        return items.filter(meeting => {
          if (!meeting.date || !meeting.time) return false;
          const meetingDate = new Date(`${meeting.date}T${meeting.time}`);
          return meetingDate > now;
        });
      case 'past':
        return items.filter(meeting => {
          if (!meeting.date || !meeting.time) return false;
          const meetingDate = new Date(`${meeting.date}T${meeting.time}`);
          return meetingDate <= now;
        });
      default:
        return items.filter(meeting => {
          if (!meeting.date || !meeting.time) return false;
          const meetingDate = new Date(`${meeting.date}T${meeting.time}`);
          return meetingDate > now;
        });
    }
  }, [activeTab, items, userAttendance]);

  const getMeetingTypeLabel = (type: string) => {
    const meetingType = meetingTypes.find((t: { value: string; label: string }) => t.value === type);
    return meetingType ? meetingType.label : type;
  };

  const getMeetingTypeColor = (type: string) => {
    switch (type) {
      case 'AGM':
      case 'Norway AGM':
        return 'text-blue-600';
      case 'EGM':
      case 'Norway EGM':
        return 'text-purple-600';
      default:
        return 'text-emerald-600';
    }
  };

  const getVenueIcon = (venue: string) => {
    switch (venue) {
      case 'Virtual':
        return Monitor;
      case 'In-Person':
        return MapPin;
      case 'Hybrid':
        return Users;
      default:
        return MapPin;
    }
  };

  return {
    activeTab,
    setActiveTab,
    userAttendance,
    setUserAttendance,
    items,
    setItems,
    isLoading,
    error,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleAttendanceChange,
    getFilteredMeetings,
    getMeetingTypeLabel,
    getMeetingTypeColor,
    getVenueIcon,
  };
}; 